import { combineReducers } from 'redux'
import _, { difference, isArray, isObject, values, keys, cloneDeep, omitBy  } from 'lodash'

import ActionTypes from '../../actions/ActionTypes'
import { handleAPIError } from '../../lib/error'
import { sendUpdate } from '../../lib/APIClient'

/**
 * this reducer is gonna change when a new duck is added
 */

// this reducer will be replaced when the ducks are added to it
let _reducer = combineReducers({ saving, dirty })
const duckNames = new Set()

export const reducer = (state, action) => _reducer(state, action)

/**
 * Actions
 */

const changesRE = /^(changed|created|removed).*/

function thereAreChanges(persistence) {
  for (let prop in persistence) {
    if (changesRE.test(prop)) {
      const category = persistence[prop]
      if (isArray(category) && category.length) return true
      if (isObject(category) && values(category).length) return true
    }
  }

  return false
}

/* Update the DB */

const savingAction = () => ({ type: ActionTypes.SAVING })
const savingDone = () => ({ type: ActionTypes.SAVING_DONE })
const savingFailed = (error => ({ type: ActionTypes.SAVING_FAILED, payload: error }))

const dirtyAction = (value => ({ type: ActionTypes.DIRTY_PERSISTENCE, value }))

function getUpdateForCollection(data, createdIds, updated, removedIds) {
  const toRemove = difference(removedIds, createdIds)
  const toCreate = difference(createdIds, removedIds)
  const fUpdated = omitBy(updated, (v, k) => toRemove.indexOf(k) == -1)

  return {
    c: toCreate.length ? toCreate.map(id => data[id]) : null,
    d: toRemove.length ? toRemove : null,
    u: keys(updated).length ? updated : null,
  }
}

export const saveChanges = () => (dispatch, getState) => {
  const state = getState()

  // @TODO por qué hay thereAreChanges Y somethingChanged ?

  if (!thereAreChanges(getState().persistence)
    || getState().persistence.saving) {
    return
  }

  const p = cloneDeep(state.persistence)

  dispatch(savingAction())

  /* Unified Update: a single api endpoint can do all the CRUD you may want, at once
   * it receives an object with .c.thing, .u.thing and .d.thing */
  const unifiedUpdate = { c: {}, u: {}, d: {}}
  let somethingChanged = false

  /* NOTE about that name.toLowerCase
   * yea... so, the nane of the collection was in upper case, bc it was a constant string
   * but here it's just the name of the mongo collection, which is just in lower case
   * i've decided that the API is gonna take lower case, and thus changing it is
   * the client's responsibility */

  const saveCollection = (name) => {
    const uni = getUpdateForCollection(state[name.toLowerCase()], p[`created${name}`], p[`changed${name}`], p[`removed${name}`])
    if (uni.c) { unifiedUpdate.c[name.toLowerCase()] = uni.c; somethingChanged = true; }
    if (uni.u) { unifiedUpdate.u[name.toLowerCase()] = uni.u; somethingChanged = true; }
    if (uni.d) { unifiedUpdate.d[name.toLowerCase()] = uni.d; somethingChanged = true; }
  }

  duckNames.forEach(duckName => saveCollection(duckName))

  if (somethingChanged) {
    sendUpdate(unifiedUpdate)
      .then(
        () => {
          dispatch(savingDone())
          dispatch(dirtyAction(false))
        },
        reason => {
          console.error(`Persistence's apiPromises rejected: ${reason}`)
          alert(`Error guardando los datos: ${reason}`)
          handleAPIError(reason)
          dispatch(savingFailed(new Error(reason)))
        }
      )
  }
}

/**
 * Simple sub-reducers
 */

/* NOTE
 * there are 2 ways of knowing an action makes the state dirty: via a regex, and by checking it against a set
 * the second one is my prefered one, although the first one has its appeal
 * previously we were using both, which wasnt great.
 * if (or rather "when") there are bugs, consider this change.
 */
const dirtyActions = new Set()
const dirtyRegex = /^(REMOVE_|CREATE_|UPDATE_|UPDATED_).*/
function isDirtyAction(action) {
  return dirtyActions.has(action.type) // || dirtyRegex.test(action.type)
}

function dirty(state = false, action) {
  if (action.type == ActionTypes.DIRTY_PERSISTENCE) {
    return action.value
  }
  else return state || isDirtyAction(action)
}

function saving(state = false, action) {
  if (action.type == ActionTypes.SAVING) {
    return true
  }
  else if (
    // TODO deal with a saving failure
    action.type == ActionTypes.SAVING_FAILED
    || action.type == ActionTypes.SAVING_DONE
  ) {
    return false
  }
  else {
    return state
  }
}

/**
 * ducky subreducers
 */

function createdX(actionsToWatchFor) {
  return (state = [], action) => {
    if (action.type == ActionTypes.SAVING) {
      return []
    }
    else if (actionsToWatchFor.indexOf(action.type) > -1) {
      return state.concat(action.id)
    }
    else return state
  }
}

function changedX(name: string): Object {
  return (state = {}, action) => {
    if (action.dontPersist) return state

    if (action.type == `UPDATE_ONE_${name}` || action.type == `UPDATED_ONE_${name}`) {
      const changes = action.payload
      if (!changes) return state

      if (state[action.id]) {
        return { ...state, [action.id]: { ...state[action.id], ...changes } }
      }

      return { ...state, [action.id]: changes }
    }
    else if (action.type == `UPDATE_MANY_${name}`) {
      const newChanges = {}

      action.id.forEach(id => {
        if (state[id]) newChanges[id] = { ...state[id], ...action.payload }
        else newChanges[id] = { ...action.payload }
      })

      return { ...state, ...newChanges }
    }
    else if (action.type == ActionTypes.SAVING) {
      return {};
    }
    else {
      return state;
    }
  }
}

const removedX = (actionToWatchFor) => (state = [], action) => {
  if (action.type == ActionTypes.SAVING) return []

  else if (action.type == actionToWatchFor) {
    const foundIndex = state.lastIndexOf(action.id)
    if ((foundIndex == -1)) return state.concat(action.id);
    else return state
  }

  else return state
}

const duckReducers = {}

export function persistDuck(name, opt = {}) {
  const created = createdX([`CREATE_ONE_${name}`, ...(opt.extraCreateActions || []) ])
  const changed = changedX(name)
  const removed = removedX(`REMOVE_ONE_${name}`)

  duckReducers[`created${name}`] = created
  duckReducers[`changed${name}`] = changed
  duckReducers[`removed${name}`] = removed

  duckNames.add(name)

  // @TODO unelegant. makes me unhappy. improve this.
  _reducer = combineReducers({ ...duckReducers, saving, dirty })

  // add action types to the list
  dirtyActions.add(`CREATE_ONE_${name}`)
  dirtyActions.add(`UPDATE_ONE_${name}`)
  dirtyActions.add(`UPDATE_MANY_${name}`)
  dirtyActions.add(`UPDATED_ONE_${name}`)
  dirtyActions.add(`REMOVE_ONE_${name}`)

  if (opt.extraCreateActions) {
    opt.extraCreateActions.map(type => dirtyActions.add(type))
  }
}
